import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Button, useMediaQuery, useTheme, CircularProgress } from "@material-ui/core";
import { useAppSelector, useAppDispatch } from "../../redux/typedHooks";
import { setUser } from "../../redux/modules/userSlice";
import { incrementGuideStepAPI, updateGuideStepsVisibilityAPI, getUserPersonalInfo } from "../../api/userApi";
import { UserInterface } from "../../types/user";
import { showToast } from "../../utils/toast";
import { useHistory } from "react-router-dom";
import { getUserType } from "../../utils/getUserType";

export interface GuideStep {
  id: number;
  title: string;
  description: string;
  targetSelector?: string; // CSS selector для елемента, навколо якого показувати гайд
  targetRef?: React.RefObject<any>; // Ref для елемента, навколо якого показувати гайд (використовується any для підтримки різних типів HTML елементів)
  position?: "top" | "bottom" | "left" | "right" | "center";
  offset?: { x: number; y: number };
}

interface GuideStepsProps {
  steps: GuideStep[];
  pageType: "home" | "profile" | "form";
  stepOffset?: number; // Початковий offset для кроків цієї сторінки
  onComplete?: () => void;
}

const GuideSteps: React.FC<GuideStepsProps> = ({ steps, pageType, stepOffset = 0, onComplete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();
  const history = useHistory();
  const user = useAppSelector<UserInterface | null>(state => state.user.currentUser);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const originalParentRef = useRef<HTMLElement | null>(null);

  // Перевіряємо чи потрібно показувати гайд-степи
  useEffect(() => {
    // Перевіряємо чи користувач є viewer (guide тільки для viewer)
    const isViewer = getUserType(user)?.title?.toLowerCase() === "viewer";
    
    // Якщо isShowGuideSteps undefined, вважаємо що гайд потрібно показувати якщо currentStepGuide >= stepOffset
    const shouldShowGuide = user?.isShowGuideSteps !== false; // Показуємо якщо не false (undefined або true)

    // Не показуємо гайд якщо користувач не пройшов onboarding
    if (!user || !isViewer || !user.isOnboarding || (!shouldShowGuide && user.isShowGuideSteps === false)) {
      setIsVisible(false);
      return;
    }

    // Визначаємо початковий крок на основі currentStepGuide
    // currentStepGuide - це глобальний лічильник (за замовчуванням 1)
    // stepOffset - це початковий offset для кроків цієї сторінки
    const globalStep = user.currentStepGuide || 1;
    
    // Перевіряємо чи є степи для цієї сторінки
    if (steps.length === 0) {
      setIsVisible(false);
      return;
    }

    // Визначаємо локальний крок для цієї сторінки
    // Якщо глобальний крок менший за offset, не показуємо гайд на цій сторінці
    if (globalStep < stepOffset) {
      setIsVisible(false);
      return
    }

    // Обчислюємо локальний крок для цієї сторінки
    const localStep = globalStep - stepOffset;
    
    // Якщо локальний крок більший або дорівнює кількості кроків на сторінці, не показуємо гайд
    if (localStep >= steps.length) {
      setIsVisible(false);
      return;
    }

    // Перевіряємо чи цільовий елемент вже є в DOM
    const checkTargetElement = () => {
      const currentStep = steps?.[localStep];
      let targetExists = false;

      if (currentStep.targetRef?.current) {
        targetExists = true;
      } else if (currentStep.targetSelector) {
        targetExists = !!document.querySelector(currentStep.targetSelector);
      } else {
        // Якщо немає target, показуємо одразу
        targetExists = true;
      }

      if (targetExists) {
        setCurrentStepIndex(localStep);
        setIsVisible(true);
      } else {
        // Якщо елемент ще не завантажився, чекаємо трохи і перевіряємо знову
        setTimeout(() => {
          checkTargetElement();
        }, 100);
      }
    };

    // Затримка для того щоб DOM встиг оновитися після редіректу
    const timeoutId = setTimeout(() => {
      checkTargetElement();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [user, steps, stepOffset]);

  // Блокуємо навігацію та скрол під час активного гайду
  useEffect(() => {
    if (!isVisible || user?.isShowGuideSteps === false) {
      // Відновлюємо скрол якщо гайд не активний
      document.body.style.overflow = "";
      return;
    }

    // Блокуємо скрол сторінки
    document.body.style.overflow = "hidden";

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();
      history.go(1); // Повертаємо назад
    };

    // Блокуємо скрол колесом миші
    const handleWheel = (e: WheelEvent) => {
      // Дозволяємо скрол тільки на tooltip
      const tooltip = tooltipRef.current;
      if (tooltip && tooltip.contains(e.target as Node)) {
        return; // Дозволяємо скрол на tooltip
      }
      e.preventDefault();
      e.stopPropagation();
    };

    // Блокуємо скрол тачжестами
    const handleTouchMove = (e: TouchEvent) => {
      const tooltip = tooltipRef.current;
      if (tooltip && tooltip.contains(e.target as Node)) {
        return; // Дозволяємо скрол на tooltip
      }
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, [isVisible, user, history]);

  // Оновлюємо позицію tooltip зі стрілкою при зміні кроку
  useEffect(() => {
    if (!isVisible || currentStepIndex >= steps.length) return;

    const currentStep = steps?.length > 0 ? steps?.[currentStepIndex] : null;

    if (!currentStep) return;
    
    // Функція для пошуку цільового елемента
    const findTargetElement = (): HTMLElement | null => {
      if (currentStep.targetRef?.current) {
        return currentStep.targetRef.current;
      } else if (currentStep.targetSelector) {
        return document.querySelector(currentStep.targetSelector) as HTMLElement;
      }
      return null;
    };

    // Функція для оновлення позиції
    const updatePosition = (element: HTMLElement) => {
      const rect = element.getBoundingClientRect();
      
      // Знаходимо батьківський контейнер з position: relative або створюємо його
      let containerElement: HTMLElement = element;
      let containerRect = rect;
      
      // Шукаємо найближчий батьківський елемент з position: relative, absolute або fixed
      let parent = element.parentElement;
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        if (style.position === 'relative' || style.position === 'absolute' || style.position === 'fixed') {
          containerElement = parent;
          containerRect = parent.getBoundingClientRect();
          break;
        }
        parent = parent.parentElement;
      }
      
      // Якщо не знайшли контейнер з position, додаємо position: relative до батьківського елемента
      if (containerElement === element && element.parentElement) {
        const parentStyle = window.getComputedStyle(element.parentElement);
        if (parentStyle.position === 'static') {
          element.parentElement.style.position = 'relative';
        }
        containerElement = element.parentElement;
        containerRect = containerElement.getBoundingClientRect();
      }
      
      // Позиціонуємо tooltip відносно контейнера
      const baseOffset = currentStep.offset || { x: 0, y: 0 };
      // Для мобільних пристроїв: якщо x вказано в конфігурації, використовуємо його, інакше 100 за замовчуванням
      // Для desktop: використовуємо значення x з конфігурації (можна міняти для зміщення вліво/вправо)
      // Для mobile також зсуваємо попап вниз (додаємо +20px до y) і вліво (зменшуємо x на 50)
      const offset = {
        x: isMobile ? ((baseOffset.x !== undefined ? baseOffset.x : 100) - 50) : baseOffset.x,
        y: isMobile ? (baseOffset.y + 20) : baseOffset.y
      };
      const viewportWidth = window.innerWidth;
      
      // Приблизні розміри tooltip
      const tooltipWidth = isMobile ? 200 : 400;
      const tooltipHeight = 200; // Приблизна висота tooltip
      const bottomOffset = 20; // Offset знизу для безпеки
      
      // Розраховуємо позицію відносно контейнера (без scrollY для absolute позиціонування)
      const elementTopRelative = rect.top - containerRect.top;
      const elementLeftRelative = rect.left - containerRect.left;
      
      let tooltipTop: number;
      let tooltipLeft: number;
      let arrowLeft: string;
      const globalStep = user?.currentStepGuide || 1;

      if (currentStep.position === "bottom") {
        // Tooltip знизу відносно елемента
        tooltipTop = elementTopRelative + rect.height + offset.y;
        tooltipLeft = elementLeftRelative + rect.width / 2 + offset.x;
        arrowLeft = "50%";

        if (globalStep === 3) {
          tooltipLeft = -120;
        }
      } else {
        // Tooltip зверху (за замовчуванням)
        // Для position: "top" з transform: translate(-50%, -100%), попап зсувається вгору на всю висоту
        // Щоб опустити попап вниз при позитивному offset.y, додаємо offset.y до позиції верху елемента
        // Це збільшує tooltipTop, що опускає попап вниз
        tooltipTop = elementTopRelative + offset.y;
        // Для кроку 3 (останній крок) встановлюємо left: 0
        if (globalStep === 3) {
          tooltipLeft = -500;
        } else if (offset.x === 0 && offset.y > 0) {
          tooltipLeft = 0;
        } else {
          tooltipLeft = elementLeftRelative + rect.width / 2 + offset.x;
        }
        arrowLeft = "50%";
      }

      // Перевіряємо чи tooltip виходить за межі екрана по горизонталі
      // Для кроку 3 пропускаємо цю перевірку, щоб дозволити left: -400
      if (globalStep !== 3) {
        const tooltipLeftMin = tooltipWidth / 2 + 16; // Мінімальна позиція зліва
        const tooltipLeftMax = viewportWidth - tooltipWidth / 2 - 16; // Максимальна позиція справа
        
        if (tooltipLeft < tooltipLeftMin) {
          tooltipLeft = tooltipLeftMin;
        } else if (tooltipLeft > tooltipLeftMax) {
          tooltipLeft = tooltipLeftMax;
        }
      }

      // Перевіряємо чи tooltip виходить за межі контейнера по вертикалі
      // Для absolute позиціонування перевіряємо відносно контейнера
      const containerHeight = containerRect.height;
      const globalStepCheck = user?.currentStepGuide || 1;
      if (currentStep.position === "bottom") {
        // Для кроку 3 пропускаємо корекцію, щоб дозволити offset.y працювати
        if (globalStepCheck !== 3) {
          const tooltipBottom = tooltipTop + tooltipHeight;
          if (tooltipBottom > containerHeight - bottomOffset) {
            tooltipTop = containerHeight - tooltipHeight - bottomOffset;
          }
        }
      } else {
        // Якщо tooltip зверху і виходить за межі контейнера - зміщуємо вниз
        if (tooltipTop < 0) {
          tooltipTop = bottomOffset;
        }
        // Також перевіряємо чи не виходить знизу контейнера
        const tooltipBottom = tooltipTop + tooltipHeight;
        if (tooltipBottom > containerHeight - bottomOffset) {
          tooltipTop = containerHeight - tooltipHeight - bottomOffset;
        }
      }

      // Розраховуємо позицію стрілки
      const targetCenterX = rect.left + rect.width / 2 + offset.x;
      const tooltipCenterX = tooltipLeft;
      const arrowOffset = targetCenterX - tooltipCenterX;
      
      if (isMobile) {
        arrowLeft = `${Math.max(8, Math.min(arrowOffset + tooltipWidth / 2, tooltipWidth - 8))}px`;
      } else {
        const arrowOffsetPercent = (arrowOffset / tooltipWidth) * 100;
        arrowLeft = `${50 + arrowOffsetPercent}%`;
      }

      // Отримуємо реальні розміри tooltip після рендерингу
      if (tooltipRef.current) {
        const actualTooltipRect = tooltipRef.current.getBoundingClientRect();
        const actualTooltipHeight = actualTooltipRect.height;
        const actualTooltipWidth = actualTooltipRect.width;
        
        // Фінальна перевірка та корекція позиції з урахуванням реальних розмірів
        const viewportWidth = window.innerWidth;
        const bottomOffset = 20; // Offset знизу для безпеки
        
        // Корекція по вертикалі відносно контейнера
        const containerBottom = containerRect.height;
        if (currentStep.position === "bottom") {
          // Для кроку 3 пропускаємо корекцію, щоб дозволити offset.y працювати
          if (globalStepCheck !== 3) {
            const tooltipBottom = tooltipTop + actualTooltipHeight;
            if (tooltipBottom > containerBottom - bottomOffset) {
              tooltipTop = containerBottom - actualTooltipHeight - bottomOffset;
            }
          }
        } else {
          // Для position: "top" з transform: translate(-50%, -100%), попап зсувається вгору на всю висоту
          // tooltipTop - це позиція низу попапу після transform
          // Верх попапу буде на позиції tooltipTop - actualTooltipHeight
          // Перевіряємо чи верх попапу не виходить за межі контейнера зверху
          const tooltipTopAfterTransform = tooltipTop - actualTooltipHeight;
          if (tooltipTopAfterTransform < bottomOffset) {
            // Якщо попап виходить за межі зверху, зміщуємо його вниз
            tooltipTop = actualTooltipHeight + bottomOffset;
          }
          // Також перевіряємо чи низ попапу не виходить знизу контейнера
          // Для position: "top" з transform, низ попапу на позиції tooltipTop
          if (tooltipTop > containerBottom - bottomOffset) {
            tooltipTop = containerBottom - bottomOffset;
          }
        }
        
        // Корекція по горизонталі
        // Для кроку 3 пропускаємо цю перевірку, щоб дозволити left: -400
        const globalStepFinal = user?.currentStepGuide || 1;
        if (globalStepFinal !== 3) {
          const tooltipLeftMin = actualTooltipWidth / 2 + 16;
          const tooltipLeftMax = viewportWidth - actualTooltipWidth / 2 - 16;
          if (tooltipLeft < tooltipLeftMin) {
            tooltipLeft = tooltipLeftMin;
          } else if (tooltipLeft > tooltipLeftMax) {
            tooltipLeft = tooltipLeftMax;
          }
        }
        
        // Встановлюємо position: absolute відносно контейнера
        tooltipRef.current.style.position = "absolute";
        
        if (currentStep.position === "bottom") {
          tooltipRef.current.style.top = `${tooltipTop}px`;
          tooltipRef.current.style.left = `${tooltipLeft}px`;
          tooltipRef.current.style.transform = "translate(-50%, 0)";
          tooltipRef.current.style.marginTop = "10px";
        } else {
          tooltipRef.current.style.top = `${tooltipTop}px`;
          tooltipRef.current.style.left = `${tooltipLeft}px`;
          // Якщо left = 0, не використовуємо transform для центрування
          if (tooltipLeft === 0) {
            tooltipRef.current.style.transform = "translate(0, -100%)";
          } else {
            tooltipRef.current.style.transform = "translate(-50%, -100%)";
          }
          tooltipRef.current.style.marginBottom = "10px";
        }
        
        // Переміщуємо tooltip всередину контейнера якщо потрібно
        if (containerElement && tooltipRef.current && !containerElement.contains(tooltipRef.current)) {
          // Зберігаємо посилання на оригінальний батьківський елемент
          if (tooltipRef.current.parentElement) {
            originalParentRef.current = tooltipRef.current.parentElement;
          }
          containerElement.appendChild(tooltipRef.current);
        }
      }

      if (arrowRef.current) {
        arrowRef.current.style.left = arrowLeft;
        if (currentStep.position === "bottom") {
          // Стрілка зверху для bottom позиції
          arrowRef.current.style.top = "-8px";
          arrowRef.current.style.bottom = "auto";
          arrowRef.current.style.borderTop = "none";
            arrowRef.current.style.borderBottom = "8px solid #1F1F1F";
        } else {
          // Стрілка знизу для top позиції
          arrowRef.current.style.top = "auto";
          arrowRef.current.style.bottom = "-8px";
          arrowRef.current.style.borderBottom = "none";
            arrowRef.current.style.borderTop = "8px solid #1F1F1F";
        }
      }
    };

    // Спочатку перевіряємо ref, потім selector
    let targetElement = findTargetElement();
    
      // Якщо елемент не знайдено, чекаємо трохи і перевіряємо знову
    if (!targetElement) {
      const checkInterval = setInterval(() => {
        targetElement = findTargetElement();
        if (targetElement) {
          clearInterval(checkInterval);
          // Оновлюємо позицію коли елемент знайдено
          updatePosition(targetElement);
          
          // Оновлюємо позицію при скролі
          const handleScroll = () => {
            const element = findTargetElement();
            if (element) updatePosition(element);
          };
          window.addEventListener("scroll", handleScroll, true);
          window.addEventListener("resize", handleScroll);

          return () => {
            window.removeEventListener("scroll", handleScroll, true);
            window.removeEventListener("resize", handleScroll);
          };
        }
      }, 100);

      // Зупиняємо перевірку через 5 секунд
      const timeoutId = setTimeout(() => {
        clearInterval(checkInterval);
      }, 5000);

      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
        // Повертаємо tooltip назад перед розмонтуванням
        const currentTooltip = tooltipRef.current;
        const currentOriginalParent = originalParentRef.current;
        if (currentTooltip && currentOriginalParent && currentTooltip.parentElement !== currentOriginalParent) {
          try {
            currentOriginalParent.appendChild(currentTooltip);
          } catch (e) {
            // Ігноруємо помилки при cleanup
          }
        }
      };
    }
    
    // Якщо елемент знайдено одразу, оновлюємо позицію
    if (targetElement) {
      updatePosition(targetElement);

      // Оновлюємо позицію при скролі
      const handleScroll = () => {
        const element = findTargetElement();
        if (element) updatePosition(element);
      };
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
        // Повертаємо tooltip назад перед розмонтуванням, щоб уникнути помилки removeChild
        const tooltip = tooltipRef.current;
        const originalParent = originalParentRef.current;
        if (tooltip && originalParent && tooltip.parentElement !== originalParent) {
          try {
            originalParent.appendChild(tooltip);
          } catch (e) {
            // Ігноруємо помилки при cleanup
          }
        }
      };
    }
  }, [currentStepIndex, isVisible, steps, isMobile]);

  // Плавний скрол до цільового елемента після завантаження, щоб tooltip був видимий
  useEffect(() => {
    if (!isVisible || currentStepIndex >= steps.length) return;

    const currentStep = steps?.[currentStepIndex];
    
    // Функція для пошуку цільового елемента
    const findTargetElement = (): HTMLElement | null => {
      if (currentStep.targetRef?.current) {
        return currentStep.targetRef.current;
      } else if (currentStep.targetSelector) {
        return document.querySelector(currentStep.targetSelector) as HTMLElement;
      }
      return null;
    };

    // Затримка перед скролом, щоб tooltip встиг відрендеритися
    const scrollTimeout = setTimeout(() => {
      const targetElement = findTargetElement();
      if (!targetElement || !tooltipRef.current) return;
      
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const bottomOffset = 20; // Offset знизу для безпеки
      
      // Перевіряємо чи tooltip повністю видимий з урахуванням offset знизу
      const isFullyVisible = 
        tooltipRect.top >= 0 &&
        tooltipRect.left >= 0 &&
        tooltipRect.bottom <= viewportHeight - bottomOffset &&
        tooltipRect.right <= viewportWidth;
      
      // Якщо tooltip не видимий, скролимо до цільового елемента
      if (!isFullyVisible) {
        let scrollY = window.scrollY;
        let scrollX = window.scrollX;
        
        // Визначаємо позицію для скролу на основі позиції tooltip
        // Якщо tooltip зверху від елемента, скролимо так щоб елемент був видимий зверху
        if (currentStep.position === "top") {
          // Скролимо так, щоб tooltip був видимий зверху
          if (tooltipRect.top < 0) {
            scrollY = window.scrollY + tooltipRect.top - 20; // Додаємо відступ зверху
          } else if (tooltipRect.bottom > viewportHeight - bottomOffset) {
            // Якщо tooltip виходить знизу, скролимо так щоб він був видимий
            scrollY = window.scrollY + tooltipRect.bottom - (viewportHeight - bottomOffset);
          }
        } else {
          // Якщо tooltip знизу, скролимо так щоб елемент був видимий знизу
          if (tooltipRect.bottom > viewportHeight - bottomOffset) {
            scrollY = window.scrollY + tooltipRect.bottom - (viewportHeight - bottomOffset);
          } else if (tooltipRect.top < 0) {
            scrollY = window.scrollY + tooltipRect.top;
          }
        }
        
        // Горизонтальний скрол
        if (tooltipRect.right > viewportWidth) {
          scrollX = window.scrollX + tooltipRect.right - viewportWidth;
        }
        if (tooltipRect.left < 0) {
          scrollX = window.scrollX + tooltipRect.left;
        }
        
        // Плавний скрол
        window.scrollTo({
          top: Math.max(0, scrollY),
          left: Math.max(0, scrollX),
          behavior: 'smooth'
        });
      }
    }, 800); // Збільшена затримка до 800мс щоб tooltip точно встиг відрендеритися

    return () => clearTimeout(scrollTimeout);
  }, [isVisible, currentStepIndex, steps]);

  const handleNext = async () => {
    if (!user || isLoading) return;

    setIsLoading(true);
    try {
      // Викликаємо API для збільшення кроку
      await incrementGuideStepAPI();
      
      // Refetch користувача щоб отримати оновлені дані
      const userResponse = await getUserPersonalInfo();

      console.log('userResponse', userResponse);
      if (userResponse?.success && userResponse?.data) {
        const updatedUserData = userResponse.data as any;
        
        // Оновлюємо користувача в Redux з оновленими даними
        dispatch(
          setUser({
            ...updatedUserData,
          } as any)
        );

        // Обчислюємо новий локальний крок на основі оновлених даних
        const newGlobalStep = updatedUserData.currentStepGuide || 1;
        const newLocalStep = newGlobalStep - stepOffset;

        // Перевіряємо чи це останній крок для цієї сторінки
        if (newLocalStep >= steps.length) {
          setIsVisible(false);
          onComplete?.();
        } else {
          setCurrentStepIndex(newLocalStep);
        }
      } else {
        // Якщо refetch не вдався, використовуємо оптимістичне оновлення
        const newGlobalStep = (user.currentStepGuide || 1) + 1;
        dispatch(
          setUser({
            ...user,
            currentStepGuide: newGlobalStep,
          } as any)
        );
        
        const newLocalStep = newGlobalStep - stepOffset;
        if (newLocalStep >= steps.length) {
          setIsVisible(false);
          onComplete?.();
        } else {
          setCurrentStepIndex(newLocalStep);
        }
      }
    } catch (error) {
      console.error("Failed to increment guide step:", error);
      showToast("Помилка оновлення кроку гайду", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    if (!user || isLoading) return;

    setIsLoading(true);
    try {
      await updateGuideStepsVisibilityAPI(false);
      
      // Оновлюємо користувача в Redux
      dispatch(
        setUser({
          ...user,
          isShowGuideSteps: false,
        } as any)
      );

      setIsVisible(false);
      onComplete?.();
    } catch (error) {
      console.error("Failed to skip guide steps:", error);
      showToast("Помилка пропуску гайду", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipOnboarding = handleSkip;

  if (!isVisible || !user || currentStepIndex >= steps.length) {
    return null;
  }

  const currentStep = steps?.length > 0 ? steps?.[currentStepIndex] : null;

  if (!currentStep) return null;

  const globalStep = (user?.currentStepGuide || 1);
  const totalSteps = 3; // Загальна кількість кроків
  // Перевіряємо чи це останній глобальний крок (3), а не локальний
  const isLastStep = globalStep === totalSteps;

  return (
    <>
      {/* Overlay який блокує всі кліки поза tooltip */}
      <div
        ref={overlayRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9998,
          pointerEvents: "auto",
          backgroundColor: "transparent", // Без затемнення
        }}
        onClick={(e) => {
          // Блокуємо всі кліки на overlay
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          // Блокуємо всі кліки на overlay
          e.preventDefault();
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          // Блокуємо всі тачі на overlay
          e.preventDefault();
          e.stopPropagation();
        }}
      />
      {/* Tooltip зі стрілкою зверху */}
      <div
        ref={tooltipRef}
        style={{
          position: "absolute",
          zIndex: 9999,
          backgroundColor: "#1F1F1F",
          borderRadius: "12px",
          padding: isMobile ? "20px 16px" : "24px",
          width: "100%",
          maxWidth: isMobile ? "calc(100vw - 24px)" : "400px",
          minWidth: isMobile ? "calc(100vw - 24px)" : "400px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
          pointerEvents: "auto",
          marginBottom: "10px",
          maxHeight: "80vh",
          // overflowY: "auto",
          // overflowX: "visible",
        }}
        className={`guide-tooltip ${globalStep === 3 ? 'guide-tooltip-triangle-right' : ''}`}
        onClick={(e) => {
          // Дозволяємо кліки всередині tooltip
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          // Дозволяємо кліки всередині tooltip
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          // Дозволяємо тачі всередині tooltip
          e.stopPropagation();
        }}
        onWheel={(e) => {
          // Дозволяємо скрол на tooltip
          e.stopPropagation();
        }}
        onTouchMove={(e) => {
          // Дозволяємо скрол на tooltip
          e.stopPropagation();
        }}
      >
        {/* Step indicator */}
        <Typography
          style={{
            fontSize: isMobile ? "12px" : "20px",
            color: "#747474",
            fontWeight: isMobile ? 700 : 600,
            marginBottom: "8px",
          }}
        >
          {globalStep}/{totalSteps}
        </Typography>

        {/* Title */}
        <Typography
          style={{
            fontSize: isMobile ? "14px" : "24px",
            fontWeight: 700,
            color: "#FFFFFF",
            marginBottom: "8px",
          }}
        >
          {currentStep.title}
        </Typography>

        {/* Description */}
        <Typography
          style={{
            fontSize: isMobile ? "12px" : "16px",
            fontWeight: 400,
            color: "#CCCCCC",
            marginBottom: "20px",
            lineHeight: 1.5,
          }}
        >
          {currentStep.description}
        </Typography>

        {/* Buttons */}
        <Box
          style={{
            display: "flex",
            gap: "12px",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
              <Button
                onClick={handleSkipOnboarding}
                disabled={isLoading}
                style={{
                  borderColor: "#FE5A97",
                  color: "#FE5A97",
                  padding: isMobile ? "8px 16px" : "10px 20px",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: 600,
                  flex: isMobile ? 1 : "none",
                  border: "1px solid #FE5A97",
                  backgroundColor: "transparent",
                }}
              >
                {isLoading ? (
                  <CircularProgress size={16} style={{ color: "#FE5A97" }} />
                ) : (
                  "Skip Onboarding"
                )}
              </Button>
              <Button
                onClick={handleNext}
                disabled={isLoading}
                style={{
                  backgroundColor: "#FE5A97",
                  color: "#FFFFFF",
                  padding: isMobile ? "8px 16px" : "10px 20px",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: 600,
                  flex: isMobile ? 1 : "none",
                }}
              >
                {isLoading ? (
                  <CircularProgress size={16} style={{ color: "#FFFFFF" }} />
                ) : (
                  isLastStep ? "Finish" : "Next"
                )}
              </Button>
        </Box>
      </div>
    </>
  );
};

// Стилі для трикутника через ::before
const tooltipStyles = `
  .guide-tooltip::before {
    content: '';
    position: absolute;
    top: -14px;
    left: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0px 22px 22px 0px;
    border-color: transparent transparent #1F1F1F transparent;
    transform: rotate(0deg);
    z-index: 10001;
    pointer-events: none;
  }
  .guide-tooltip-triangle-right::before {
    left: auto;
    right: 0;
    border-width: 0px 0px 22px 22px;
    border-color: transparent transparent #1F1F1F transparent;
  }
`;

// Додаємо стилі до документа
if (typeof document !== 'undefined') {
  const styleId = 'guide-tooltip-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = tooltipStyles;
    document.head.appendChild(style);
  }
}

export default GuideSteps;

